/**
 * ORBIS Admin - Auth Store
 * 
 * v2.8 Role Model:
 * - ORBIS Internal: ADMIN, REVIEWER, OPERATIONS
 * - Company Users: COMPANY_ADMIN, COMPANY_USER (require client_id)
 * - Special: REGULATOR (require regulator_authority_id)
 * 
 * Per USER_STORIES_v2.8:
 * - CL-061: Role scope is Client-global (no per-project role overrides)
 * - CL-068: Company users can see all products owned by their Company
 * - US-9.2: Organization type is an attribute of Client, not a user role
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User } from '@/types';
import api from '@/services/api';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!user.value);
  
  // ===========================================
  // ORBIS INTERNAL ROLE CHECKS
  // ===========================================
  
  const isAdmin = computed(() => user.value?.role === 'ADMIN');
  const isReviewer = computed(() => user.value?.role === 'REVIEWER');
  const isOperations = computed(() => {
    const role = user.value?.role || '';
    // Operations role or legacy roles that map to operations
    return role === 'OPERATIONS' || 
           ['VIEWER', 'EDITOR'].includes(role);
  });
  
  // Is this an ORBIS internal user? (Admin, Reviewer, or Operations)
  const isOrbisUser = computed(() => 
    isAdmin.value || isReviewer.value || isOperations.value
  );
  
  // ===========================================
  // COMPANY ROLE CHECKS (v2.8)
  // ===========================================
  
  // Company Admin - full access within their Company
  const isCompanyAdmin = computed(() => {
    const role = user.value?.role || '';
    return role === 'COMPANY_ADMIN' || role === 'CLIENT';
  });
  
  // Company User - any company role (COMPANY_ADMIN or COMPANY_USER)
  const isCompanyUser = computed(() => {
    const role = user.value?.role || '';
    return ['COMPANY_ADMIN', 'COMPANY_USER', 'CLIENT', 'MANUFACTURER'].includes(role);
  });
  
  // Get the user's client_id (for Company users)
  const userClientId = computed(() => user.value?.client_id);
  
  // Get the user's client name
  const userClientName = computed(() => user.value?.client_name);
  
  // ===========================================
  // COMPANY TYPE FLAGS (v2.8)
  // What type of company does this user belong to?
  // ===========================================
  
  // User's company is a manufacturer
  const companyIsManufacturer = computed(() => user.value?.client_is_manufacturer || false);
  
  // User's company is a laboratory
  const companyIsLaboratory = computed(() => user.value?.client_is_laboratory || false);
  
  // User's company is an importer
  const companyIsImporter = computed(() => user.value?.client_is_importer || false);
  
  // User's company is a lab or importer (not a manufacturer) - shows "Linked Manufacturers" nav
  const companyIsLabOrImporter = computed(() => 
    (companyIsLaboratory.value || companyIsImporter.value) && !companyIsManufacturer.value
  );
  
  // ===========================================
  // REGULATOR ROLE CHECKS
  // ===========================================
  
  const isRegulator = computed(() => {
    const role = user.value?.role || '';
    return role === 'REGULATOR' || !!user.value?.regulator_authority_id;
  });
  
  // ===========================================
  // USER PERMISSION FLAGS (v2.8)
  // ===========================================
  
  // User has approver permission
  const userIsApprover = computed(() => user.value?.is_approver || false);
  
  // User can publish products
  const userCanPublishProducts = computed(() => user.value?.can_publish_products || false);
  
  // User can manage regulator grants
  const userCanManageGrants = computed(() => user.value?.can_manage_grants || false);
  
  // ===========================================
  // PERMISSION CHECKS
  // ===========================================
  
  // Can the user publish products?
  // v2.9 (CL-084): ONLY ORBIS Admin/Reviewer can publish
  // Company Admin CANNOT publish - this is ORBIS-controlled
  const canPublish = computed(() => {
    return isAdmin.value || isReviewer.value;
  });
  
  // v2.9: Explicit check for ORBIS publication access
  // Use this for showing/hiding publication UI elements
  const canPublishProducts = computed(() => {
    return isAdmin.value || isReviewer.value;
  });
  
  // Can the user approve documents?
  // - ORBIS Admin/Reviewer: Can approve any document
  // - COMPANY_ADMIN: Can approve their Company's documents
  // - COMPANY_USER: Can approve if is_approver=true and Client policy allows
  const canApprove = computed(() => {
    if (isAdmin.value || isReviewer.value) return true;
    if (isCompanyAdmin.value) return true;
    if (isCompanyUser.value && userIsApprover.value) return true;
    return false;
  });
  
  // Can the user manage users? (ORBIS Admin only for all users)
  const canManageUsers = computed(() => isAdmin.value);
  
  // Can the user manage Company users? (Company Admin can manage their Company's users)
  const canManageCompanyUsers = computed(() => isAdmin.value || isCompanyAdmin.value);
  
  // Can the user approve client registrations?
  const canApproveRegistrations = computed(() => 
    isAdmin.value || isReviewer.value
  );
  
  // Can the user manage clients (companies)?
  const canManageClients = computed(() => isAdmin.value || isReviewer.value);
  
  // Can the user see all products (not just their Company's)?
  const canSeeAllProducts = computed(() => isOrbisUser.value);
  
  // Can the user see all audit logs?
  const canSeeAllAuditLogs = computed(() => isOrbisUser.value);
  
  // Can the user manage regulator grants?
  const canManageRegulatorGrants = computed(() => {
    if (isAdmin.value) return true;
    if (isCompanyAdmin.value) return true;
    if (isCompanyUser.value && userCanManageGrants.value) return true;
    return false;
  });
  
  // ===========================================
  // LEGACY/DEPRECATED ROLE CHECKS
  // ===========================================
  // These check for deprecated role values during migration.
  // Use isCompanyUser instead for new code.
  
  // DEPRECATED: Use isCompanyUser instead
  const isClientUser = computed(() => isCompanyUser.value);
  
  // DEPRECATED: Organization type is now on Client, not user role
  const isManufacturer = computed(() => user.value?.role === 'MANUFACTURER');
  const isImporter = computed(() => user.value?.role === 'IMPORTER');
  const isLaboratory = computed(() => user.value?.role === 'LABORATORY');
  const isEditor = computed(() => ['ADMIN', 'REVIEWER', 'OPERATIONS', 'EDITOR'].includes(user.value?.role || ''));

  // Get user's accessible entities
  const userEntities = computed(() => {
    if (!user.value?.entity_relationships) return [];
    return user.value.entity_relationships.filter(rel => rel.is_active);
  });

  const accessibleManufacturers = computed(() => {
    if (isAdmin.value) return []; // Admin sees all
    if (isManufacturer.value) {
      return userEntities.value
        .filter(rel => rel.entity_type === 'MANUFACTURER')
        .map(rel => rel.entity_id);
    }
    return []; // Others can't see manufacturers
  });

  const accessibleImporters = computed(() => {
    if (isAdmin.value) return []; // Admin sees all
    if (isImporter.value) {
      return userEntities.value
        .filter(rel => rel.entity_type === 'IMPORTER')
        .map(rel => rel.entity_id);
    }
    return []; // Others can't see importers
  });

  const accessibleLaboratories = computed(() => {
    if (isAdmin.value) return []; // Admin sees all
    if (isLaboratory.value) {
      return userEntities.value
        .filter(rel => rel.entity_type === 'LABORATORY')
        .map(rel => rel.entity_id);
    }
    return []; // Others can't see laboratories
  });

  async function login(email: string, password: string) {
    loading.value = true;
    error.value = null;

    try {
      const tokens = await api.login(email, password);
      api.setTokens(tokens);
      await fetchUser();
      return true;
    } catch (e: any) {
      error.value = e.response?.data?.detail || 'Login failed';
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function fetchUser() {
    try {
      user.value = await api.getCurrentUser();
    } catch {
      user.value = null;
    }
  }

  async function logout() {
    try {
      await api.logout();
    } finally {
      user.value = null;
      api.clearTokens();
    }
  }

  async function checkAuth() {
    const token = localStorage.getItem('access_token');
    if (token) {
      await fetchUser();
    }
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    
    // ===========================================
    // ORBIS INTERNAL ROLES
    // ===========================================
    isAdmin,
    isReviewer,
    isOperations,
    isOrbisUser,
    
    // ===========================================
    // COMPANY ROLES (v2.8)
    // ===========================================
    isCompanyAdmin,
    isCompanyUser,
    userClientId,
    userClientName,
    
    // ===========================================
    // COMPANY TYPE FLAGS (v2.8)
    // ===========================================
    companyIsManufacturer,
    companyIsLaboratory,
    companyIsImporter,
    companyIsLabOrImporter,
    
    // ===========================================
    // REGULATOR ROLES
    // ===========================================
    isRegulator,
    
    // ===========================================
    // USER PERMISSION FLAGS (v2.8)
    // ===========================================
    userIsApprover,
    userCanPublishProducts,
    userCanManageGrants,
    
    // ===========================================
    // PERMISSION CHECKS
    // ===========================================
    canPublish,
    canPublishProducts, // v2.9: Explicit ORBIS-only publication check
    canApprove,
    canManageUsers,
    canManageCompanyUsers,
    canApproveRegistrations,
    canManageClients,
    canSeeAllProducts,
    canSeeAllAuditLogs,
    canManageRegulatorGrants,
    
    // ===========================================
    // LEGACY/DEPRECATED (backward compatibility)
    // ===========================================
    isClientUser, // DEPRECATED: Use isCompanyUser
    isManufacturer,
    isImporter,
    isLaboratory,
    isEditor,
    userEntities,
    accessibleManufacturers,
    accessibleImporters,
    accessibleLaboratories,
    
    // ===========================================
    // ACTIONS
    // ===========================================
    login,
    logout,
    fetchUser,
    checkAuth,
  };
});

